import React from 'react';
import { useMobileChart } from '../../context/MobileChartContext';

interface FactorTriggerProps {
  factorName: string;
  value: number | string;
  status?: 'positive' | 'negative' | 'neutral' | 'warning';
  description?: string;
  chartComponent: React.ReactNode;
  className?: string;
}

/**
 * A component that displays a factor and triggers a chart overlay on mobile
 * when tapped. On desktop, this component does not change the existing behavior.
 */
const FactorTrigger: React.FC<FactorTriggerProps> = ({
  factorName,
  value,
  status = 'neutral',
  description,
  chartComponent,
  className = '',
}) => {
  const { isMobile, openChart } = useMobileChart();
  
  // Define colors based on status
  const statusColors = {
    positive: 'text-green-600',
    negative: 'text-red-600',
    warning: 'text-amber-600',
    neutral: 'text-slate-600'
  };
  
  const textColor = statusColors[status];
  
  const handleClick = () => {
    if (isMobile) {
      openChart('custom', factorName, chartComponent);
    }
  };
  
  return (
    <div 
      className={`${className} ${isMobile ? 'cursor-pointer active:bg-slate-50' : ''}`}
      onClick={isMobile ? handleClick : undefined}
    >
      <div className="flex flex-col">
        <div className="font-medium text-slate-700">{factorName}</div>
        <div className={`text-xl font-bold ${textColor}`}>{value}</div>
        {description && (
          <div className="text-sm text-slate-500">{description}</div>
        )}
        {isMobile && (
          <div className="text-xs text-blue-600 mt-1">Tap to view chart</div>
        )}
      </div>
    </div>
  );
};

export default FactorTrigger; 